import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { OnboardingAnswer, WorldviewExtractResponse } from '@arena/shared';
import { saveWorldview } from '../lib/storage';

const API_BASE =
  (import.meta.env.VITE_API_BASE as string | undefined) ?? 'http://localhost:8787';

const PROMPT = `Write a comprehensive .md file of my worldview:
# [Name]'s Brain
## How I Think + What I Value
(deepest frames, priors, reasoning style)
## My Views
(stances on domains — each with its why, tracing back to a frame above)
## What Formed Me
(experiences that produced the frames)
## What I Know
(facts, domain expertise)`;

/**
 * Onboarding questions. Five high signal-per-question prompts that probe
 * priority ordering, epistemics, tribal independence, aesthetics, and
 * self-model. The answers are stored raw on the worldview and ALSO sent to
 * the extraction endpoint, which converts them into an abstract "How I
 * Reason" section that gets appended to the markdown.
 *
 * The abstraction step is what prevents the chat agent from parroting
 * specific picks back at the user — only the distilled patterns reach the
 * chat prompt.
 */
type QuestionType = 'mc-and-why' | 'short';

interface Question {
  id: string;
  prompt: string;
  type: QuestionType;
  /** Hint shown under the prompt as a placeholder. */
  whyPlaceholder: string;
  /** Required for `mc-and-why`; absent for `short`. */
  options?: string[];
}

const QUESTIONS: Question[] = [
  {
    id: 'tradeoff',
    prompt: 'When two things you value conflict, which usually wins?',
    type: 'mc-and-why',
    options: [
      'Truth, even when it hurts',
      'Kindness, even when it bends truth',
      'Loyalty to people over abstract principle',
      'Principle over loyalty',
    ],
    whyPlaceholder: 'Why?',
  },
  {
    id: 'epistemics',
    prompt: "On a topic you don't know well, how do you decide who to trust?",
    type: 'mc-and-why',
    options: [
      'Their track record on related calls',
      'Reasoning quality, regardless of credentials',
      'Credentials and institutional standing',
      'Whether their incentives align with truth',
    ],
    whyPlaceholder: 'Why?',
  },
  {
    id: 'disagreement',
    prompt:
      'Name a belief common among people like you that you think is wrong. Why is it wrong?',
    type: 'short',
    whyPlaceholder: 'The belief, and why it’s wrong…',
  },
  {
    id: 'aesthetic',
    prompt:
      "When you say something is good — a book, a product, a person's work — what are you usually responding to?",
    type: 'mc-and-why',
    options: [
      'Craft and execution',
      'Originality',
      'Emotional impact',
      'Insight — what it taught me',
    ],
    whyPlaceholder: 'Why?',
  },
  {
    id: 'self-knowledge',
    prompt: 'What do people consistently misunderstand about you?',
    type: 'short',
    whyPlaceholder: 'The misunderstanding, and what’s actually true…',
  },
];

type AnswerState = Record<string, { choice?: string; why?: string }>;

type Step = 'choose' | 'paste' | 'questions' | 'saved';

interface CraftModalProps {
  open: boolean;
  onClose: () => void;
}

export function CraftModal({ open, onClose }: CraftModalProps) {
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>('choose');
  const [pasted, setPasted] = useState('');
  const [copied, setCopied] = useState(false);
  const [savedId, setSavedId] = useState<string | null>(null);
  const [answers, setAnswers] = useState<AnswerState>({});
  const [extracting, setExtracting] = useState(false);
  const [extractError, setExtractError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  useEffect(() => {
    if (!open) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [open]);

  useEffect(() => {
    if (open) return;
    const t = window.setTimeout(() => {
      setStep('choose');
      setPasted('');
      setCopied(false);
      setSavedId(null);
      setAnswers({});
      setExtracting(false);
      setExtractError(null);
    }, 300);
    return () => window.clearTimeout(t);
  }, [open]);

  if (!open) return null;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(PROMPT);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // clipboard unavailable; fall through silently
    }
  };

  const goToQuestions = () => {
    if (!pasted.trim()) return;
    setStep('questions');
  };

  const setChoice = (id: string, choice: string) => {
    setAnswers((prev) => ({
      ...prev,
      [id]: { ...prev[id], choice: prev[id]?.choice === choice ? undefined : choice },
    }));
  };

  const setWhy = (id: string, why: string) => {
    setAnswers((prev) => ({ ...prev, [id]: { ...prev[id], why } }));
  };

  const buildRawAnswers = (): OnboardingAnswer[] =>
    QUESTIONS.map((q) => {
      const a = answers[q.id];
      return {
        questionId: q.id,
        choice: a?.choice,
        why: a?.why?.trim() || undefined,
      };
    }).filter((a) => a.choice || a.why);

  const persist = (content: string, raw: OnboardingAnswer[]) => {
    const saved = saveWorldview(content, raw.length > 0 ? raw : undefined);
    setSavedId(saved.id);
    setStep('saved');
  };

  const handleSkipQuestions = () => {
    persist(pasted.trim(), []);
  };

  const handleGeneratePatterns = async () => {
    const raw = buildRawAnswers();
    if (raw.length === 0) {
      handleSkipQuestions();
      return;
    }

    setExtracting(true);
    setExtractError(null);
    try {
      const res = await fetch(`${API_BASE}/api/worldview/extract-patterns`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          answers: raw.map((a) => ({
            question: QUESTIONS.find((q) => q.id === a.questionId)?.prompt ?? a.questionId,
            choice: a.choice,
            why: a.why,
          })),
        }),
      });
      if (!res.ok) throw new Error(`extract_failed_${res.status}`);
      const data = (await res.json()) as WorldviewExtractResponse;
      const section = data.section.trim();
      const base = pasted.trim();
      const combined = section ? `${base}\n\n${section}\n` : base;
      persist(combined, raw);
    } catch (err) {
      const detail = err instanceof Error ? err.message : 'unknown_error';
      setExtractError(detail);
    } finally {
      setExtracting(false);
    }
  };

  const handleTestIt = () => {
    if (!savedId) return;
    navigate(`/worldview/chat/${savedId}`);
  };

  return (
    <div className="craft-modal" role="dialog" aria-modal="true" aria-label="Craft your worldview">
      <div className="craft-modal__backdrop" onClick={onClose} aria-hidden="true" />
      <div className="craft-modal__panel">
        <div className="craft-modal__body">
        <button
          type="button"
          className="craft-modal__close"
          onClick={onClose}
          aria-label="Close"
        >
          ×
        </button>

        {step === 'choose' && (
          <>
            <p className="craft-modal__eyebrow">— Choose your path —</p>
            <h2 className="craft-modal__title">How will you craft?</h2>
            <p className="craft-modal__subtitle">
              Two paths to the same artefact: a portable file of how you reason.
            </p>

            <div className="craft-modal__options">
              <button
                type="button"
                className="craft-option"
                onClick={() => setStep('paste')}
              >
                <span className="craft-option__eyebrow">Self-Authored</span>
                <h3 className="craft-option__title">From the Hand</h3>
                <p className="craft-option__desc">
                  Take a prompt to the LLM you already use. Answer it honestly. Bring the result back here.
                </p>
                <span className="craft-option__cta">
                  Begin <span aria-hidden="true">→</span>
                </span>
              </button>

              <button
                type="button"
                className="craft-option"
                onClick={() => {
                  onClose();
                  navigate('/worldview/analyzer');
                }}
              >
                <span className="craft-option__eyebrow">New</span>
                <h3 className="craft-option__title">The Analyzer</h3>
                <p className="craft-option__desc">
                  A guided run of concrete questions. Pick the option closest to you. We build a brain from your answers, then test it against you.
                </p>
                <span className="craft-option__cta">
                  Begin <span aria-hidden="true">→</span>
                </span>
              </button>
            </div>
          </>
        )}

        {step === 'paste' && (
          <>
            <button
              type="button"
              className="craft-modal__back"
              onClick={() => setStep('choose')}
            >
              <span aria-hidden="true">←</span> Back
            </button>

            <p className="craft-modal__eyebrow">— Step One —</p>
            <h2 className="craft-modal__title">Take this to your LLM</h2>
            <p className="craft-modal__subtitle">
              Copy the prompt below, paste it into Claude or ChatGPT, then bring the result back.
            </p>

            <div className="craft-modal__prompt">
              <pre>{PROMPT}</pre>
              <button
                type="button"
                className="craft-modal__copy"
                onClick={handleCopy}
              >
                {copied ? 'Copied' : 'Copy prompt'}
              </button>
            </div>

            <p className="craft-modal__eyebrow">— Step Two —</p>
            <h2 className="craft-modal__title craft-modal__title--small">
              Paste your worldview
            </h2>

            <textarea
              className="craft-modal__textarea"
              value={pasted}
              onChange={(e) => setPasted(e.target.value)}
              placeholder="# Your name's brain..."
              rows={12}
              spellCheck={false}
            />

            <button
              type="button"
              className="craft-modal__save"
              onClick={goToQuestions}
              disabled={!pasted.trim()}
            >
              Continue
            </button>
          </>
        )}

        {step === 'questions' && (
          <>
            <button
              type="button"
              className="craft-modal__back"
              onClick={() => setStep('paste')}
              disabled={extracting}
            >
              <span aria-hidden="true">←</span> Back
            </button>

            <p className="craft-modal__eyebrow">— Step Three —</p>
            <h2 className="craft-modal__title">Five questions, on the house</h2>
            <p className="craft-modal__subtitle">
              Your file says what you think. These get at <em>how</em>. We distill them into
              reasoning patterns — your specific picks never reach the agent.
            </p>

            <div className="craft-questions">
              {QUESTIONS.map((q, i) => {
                const a = answers[q.id] ?? {};
                return (
                  <div key={q.id} className="craft-question">
                    <p className="craft-question__num">Question {i + 1} of {QUESTIONS.length}</p>
                    <h3 className="craft-question__prompt">{q.prompt}</h3>

                    {q.type === 'mc-and-why' && q.options && (
                      <div className="craft-question__options">
                        {q.options.map((opt) => {
                          const selected = a.choice === opt;
                          return (
                            <button
                              type="button"
                              key={opt}
                              className={
                                'craft-question__option' +
                                (selected ? ' craft-question__option--selected' : '')
                              }
                              onClick={() => setChoice(q.id, opt)}
                              disabled={extracting}
                            >
                              {opt}
                            </button>
                          );
                        })}
                      </div>
                    )}

                    <textarea
                      className="craft-question__why"
                      value={a.why ?? ''}
                      onChange={(e) => setWhy(q.id, e.target.value)}
                      placeholder={q.whyPlaceholder}
                      rows={q.type === 'short' ? 3 : 2}
                      spellCheck={false}
                      disabled={extracting}
                    />
                  </div>
                );
              })}
            </div>

            {extractError && (
              <p className="craft-question__error">
                Couldn&rsquo;t distill the patterns ({extractError}). You can retry, or skip and save the file as-is.
              </p>
            )}

            <div className="craft-modal__final-actions">
              <button
                type="button"
                className="craft-modal__save"
                onClick={handleGeneratePatterns}
                disabled={extracting}
              >
                {extracting ? 'Distilling…' : 'Finish'}
              </button>
              <button
                type="button"
                className="craft-modal__back craft-modal__back--inline"
                onClick={handleSkipQuestions}
                disabled={extracting}
              >
                Skip and save as-is
              </button>
            </div>
          </>
        )}

        {step === 'saved' && (
          <>
            <p className="craft-modal__eyebrow">— It is done —</p>
            <h2 className="craft-modal__title">Your worldview is forged</h2>
            <p className="craft-modal__subtitle">
              Stored locally on this device. Now test it — chat with an agent running your worldview and see if it reasons like you.
            </p>
            <div className="craft-modal__final-actions">
              <button
                type="button"
                className="craft-modal__save"
                onClick={handleTestIt}
                disabled={!savedId}
              >
                Test it <span aria-hidden="true">→</span>
              </button>
              <button
                type="button"
                className="craft-modal__back craft-modal__back--inline"
                onClick={onClose}
              >
                Close
              </button>
            </div>
          </>
        )}
        </div>
      </div>
    </div>
  );
}
